'use client';

import React from 'react';
import { getDeliveryLabel } from 'helpers/constants/cart';
import { CurrencyHelpers } from 'helpers/currencyHelpers';
import { formatDateTimeByLocale, formatLocalDate } from 'helpers/estimatedPickupDatehelper';
import { EU_LOCALE, getLocalizationInfo, NORTH_AMERICA_LOCALES } from 'project.config';
import { Transaction } from 'frontastic/hooks/useCart/types';
import P2RFulfillment from './P2RFulfillment';

interface Props {
  sectionMode?: 'p2rCart' | 'm2hCart';
  pickUpAtRetailAddress?: string;
  channelName?: string;
  orderDetailsData?: any;
  paymentMethod?: string;
  deliveryAddress?: string;
  shippingMethod?: string;
  estimateDateHomePickup?: string;
  locale?: string;
  shipmentInDays?: string;
  earliestPickUpText?: string;
  paymentCardImages?: any;
  freeSummary?: string;
  transactions: Transaction;
}

const OrderFulfillmentSection: React.FC<Props> = ({
  sectionMode,
  pickUpAtRetailAddress,
  channelName,
  orderDetailsData,
  paymentMethod,
  deliveryAddress,
  shippingMethod,
  estimateDateHomePickup,
  locale,
  shipmentInDays,
  earliestPickUpText,
  transactions,
  paymentCardImages,
  freeSummary,
}) => {
  const deliveryLabel = getDeliveryLabel(orderDetailsData?.shippingInfo?.name);

  const paymentDtl = orderDetailsData?.payments?.filter((item: any) => item?.customFields?.cardBrand);
  const paymentImages = paymentCardImages as any[];
  const cardImage = paymentImages?.find(
    (c: { cardName: string }) => c.cardName?.toLowerCase() === paymentDtl?.[0]?.customFields?.cardBrand?.toLowerCase(),
  );

  const freeShipping =
    (transactions.isShippingCalculated || transactions.isShippingDiscounted) && transactions.shipping.centAmount === 0;

  if (sectionMode === 'p2rCart') {
    return (
      <P2RFulfillment
        channelName={channelName}
        earliestPickUpText={earliestPickUpText}
        orderDetailsData={orderDetailsData}
        pickUpAtRetailAddress={pickUpAtRetailAddress}
      />
    );
  }

  const estimateHomePickUpDate = formatLocalDate(estimateDateHomePickup as string, locale);
  const { countryCode } = getLocalizationInfo(locale as any);
  const region = NORTH_AMERICA_LOCALES?.includes(countryCode) ? countryCode : EU_LOCALE;
  const euTimeFormat = formatDateTimeByLocale(estimateDateHomePickup as any, locale as any);

  const showPaymentMethod = Boolean(paymentDtl?.length) || (orderDetailsData?.sum?.centAmount ?? 0) > 0;

  return (
    <div className="mt-28 flex flex-col gap-16 lg:flex-row lg:gap-24" data-ffid="ctr-shipping">
      {/* LEFT COLUMN */}
      <div className="flex w-full flex-col bg-white lg:w-1/2" data-ffid="ctr-shipping-left">
        {/* PAYMENT METHOD */}
        {showPaymentMethod && (
          <div
            className="mb-16 flex items-center justify-between rounded-xl border border-[#EBEBEB] bg-white p-20 lg:mb-24"
            data-ffid="ctr-shipping-payment"
          >
            <div className="flex flex-col" data-ffid="ctr-shipping-payment-labels">
              <span className="text-14 font-semibold text-gray-800 lg:text-16" data-ffid="lbl-payment-title">
                {paymentMethod}
              </span>
              <span className="text-14 text-gray-700 first-letter:uppercase lg:text-16" data-ffid="lbl-payment-value">
                {paymentDtl[0]?.customFields?.cardBrand} {` **** `} {paymentDtl[0]?.customFields?.last4}
              </span>
            </div>

            {/* Card Logo */}
            <div
              className="flex h-28 w-48 items-center justify-center rounded border border-[#EBEBEB] p-5 font-bold text-blue-600"
              data-ffid="ctr-shipping-card-logo"
            >
              <img
                src={cardImage?.payment_Card_Image?.media?.file}
                alt="card-image"
                className="w-full"
                data-ffid="payment-card-image"
              />
            </div>
          </div>
        )}

        {/* MOBILE: SHIPPING METHOD */}
        <div className="flex gap-12 lg:hidden" data-ffid="ctr-shipping">
          {/* DELIVERY ADDRESS */}
          <div className="flex-1 rounded-xl border border-[#EBEBEB] bg-white p-16" data-ffid="ctr-shipping-address">
            <span className="mb-8 block text-14 font-semibold lg:text-16" data-ffid="lbl-shipping-address-title">
              {deliveryAddress}
            </span>

            <div className="leading-20 text-14 lg:text-16" data-ffid="ctr-shipping-address-values">
              <div data-ffid="lbl-shipping-name">{`${orderDetailsData?.shippingAddress?.firstName} ${orderDetailsData?.shippingAddress?.lastName}`}</div>
              <div data-ffid="lbl-shipping-street">{orderDetailsData?.shippingAddress?.streetName || ''} </div>
              <div data-ffid="lbl-shipping-additional-address-info">
                {orderDetailsData?.shippingAddress?.additionalAddressInfo || ''}{' '}
              </div>
              <div data-ffid="lbl-shipping-appartment">{orderDetailsData?.shippingAddress?.apartment || ''}</div>
              <div data-ffid="lbl-shipping-city">
                {`${orderDetailsData?.shippingAddress?.city ? orderDetailsData?.shippingAddress?.city : ''}, `}
                {`${orderDetailsData?.shippingAddress?.state ? orderDetailsData?.shippingAddress?.state : ''}, `}
                {`${orderDetailsData?.shippingAddress?.postalCode ? orderDetailsData?.shippingAddress?.postalCode : ''} `}
              </div>
              <div data-ffid="lbl-shipping-phone">{orderDetailsData?.shippingAddress?.phone || ''}</div>
            </div>
          </div>

          {/* SHIPPING METHOD */}
          <div className="flex-1 rounded-xl border border-[#EBEBEB] bg-white p-16" data-ffid="ctr-shipping-method">
            <span
              className="mb-8 block text-14 font-semibold text-gray-800 lg:text-16"
              data-ffid="lbl-shipping-method-title"
            >
              {shippingMethod}
            </span>

            <div className="text-14 lg:text-16" data-ffid="lbl-shipping-method-name">
              {`${deliveryLabel} -  ${freeShipping ? freeSummary : CurrencyHelpers.formatForCurrency(transactions?.shipping, locale)}`}
            </div>

            <div className="text-14 lg:text-16" data-ffid="lbl-shipping-method-estimate">
              {estimateHomePickUpDate || ''}
              {region === EU_LOCALE ? euTimeFormat : estimateHomePickUpDate}
            </div>

            <div className="text-14 lg:text-16" data-ffid="lbl-shipping-method-ships">
              {shipmentInDays}
            </div>
          </div>
        </div>

        <div
          className="hidden rounded-xl border border-[#EBEBEB] bg-white p-20 lg:block"
          data-ffid="ctr-shipping-method"
        >
          <span
            className="mb-8 block text-14 font-semibold text-gray-800 lg:text-16"
            data-ffid="lbl-shipping-method-title"
          >
            {shippingMethod}
          </span>

          <div className="text-14 lg:text-16" data-ffid="lbl-shipping-method-name">
            {`${deliveryLabel} - ${freeShipping ? freeSummary : CurrencyHelpers.formatForCurrency(transactions?.shipping, locale)}`}
          </div>

          <div className="text-14 lg:text-16" data-ffid="lbl-shipping-method-estimate">
            {region === EU_LOCALE ? euTimeFormat : estimateHomePickUpDate}
          </div>

          <div className="text-14 lg:text-16" data-ffid="lbl-shipping-method-ships">
            {shipmentInDays}
          </div>
        </div>
      </div>

      <div className="hidden w-[50%] bg-white lg:block" data-ffid="ctr-shipping-right">
        <div className="rounded-xl border border-[#EBEBEB] p-20" data-ffid="ctr-shipping-right-address">
          <span
            className="mb-8 block text-14 font-semibold text-gray-800 lg:text-16"
            data-ffid="lbl-shipping-address-title"
          >
            {deliveryAddress}
          </span>

          <div className="leading-22 text-14 text-gray-700 lg:text-16" data-ffid="ctr-shipping-right-values">
            <div data-ffid="lbl-shipping-name">{`${orderDetailsData?.shippingAddress?.firstName} ${orderDetailsData?.shippingAddress?.lastName}`}</div>
            <div data-ffid="lbl-shipping-street">{orderDetailsData?.shippingAddress?.streetName || ''}</div>
            <div data-ffid="lbl-shipping-additional-address-info">
              {orderDetailsData?.shippingAddress?.additionalAddressInfo || ''}{' '}
            </div>
            <div data-ffid="lbl-shipping-appartment">{orderDetailsData?.shippingAddress?.apartment || ''}</div>
            <div data-ffid="lbl-shipping-city">
              {`${orderDetailsData?.shippingAddress?.city ? orderDetailsData?.shippingAddress?.city : ''}, `}
              {`${orderDetailsData?.shippingAddress?.state ? orderDetailsData?.shippingAddress?.state : ''}, `}
              {`${orderDetailsData?.shippingAddress?.postalCode ? orderDetailsData?.shippingAddress?.postalCode : ''} `}
            </div>
            <div data-ffid="lbl-shipping-phone">{orderDetailsData?.shippingAddress?.phone || ''}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderFulfillmentSection;
